/**
 * MandatCacLectureService — lectures des mandats CAC servant les alertes et les listes détaillées.
 *
 * Avant la refonte d'octobre 2026, le mandat titulaire ne portait que l'UUID de son mandat
 * suppléant : il fallait un `retrieve` par suppléant absent de la page pour restituer son nom.
 * Le mandat porte désormais la paire, donc ces appels supplémentaires ont disparu.
 * Consommé via la façade `CacApiService`.
 */

import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { MAX_PAGE_SIZE } from '../../../../shared/core/services/pagination.util';
import { fetchAll } from '../../../../shared/rx';
import { construireAlertes } from '../cac-alertes.helpers';
import { toApiDate, toMandatDetaille } from '../cac-api.mappers';
import type { MandatCommissaireAuxComptes } from '../models/cac-api.model';
import type { AlerteMandat, MandatDetailleCac } from '../models/cac.model';
import { type MandatCacListFilters, MandatCommissaireComptesService } from './mandat-commissaire-comptes.service';

/** Horizon des alertes de fin de mandat (R-CAC-02 : 90 / 60 / 30 jours). */
const HORIZON_ALERTE_JOURS = 90;

@Injectable({ providedIn: 'root' })
export class MandatCacLectureService {
    private readonly mandatService = inject(MandatCommissaireComptesService);

    /** Mandats échéant dans les 90 jours, avec le suppléant que chacun désigne. */
    listAlertes(): Observable<AlerteMandat[]> {
        const today = new Date();
        const horizon = new Date(today);
        horizon.setDate(horizon.getDate() + HORIZON_ALERTE_JOURS);
        return this.lister({ date_fin__gte: toApiDate(today), date_fin__lte: toApiDate(horizon) }).pipe(
            map((mandats) => construireAlertes(mandats, today)),
        );
    }

    /** Mandats un par un (porteur, société, période, suppléant) — répertoire et fiche personne. */
    listDetailles(filters: MandatCacListFilters = {}): Observable<MandatDetailleCac[]> {
        return this.lister(filters).pipe(map((mandats) => mandats.map(toMandatDetaille)));
    }

    private lister(filters: MandatCacListFilters): Observable<MandatCommissaireAuxComptes[]> {
        return fetchAll((page) => this.mandatService.list({ ...filters, page, per_page: MAX_PAGE_SIZE })).pipe(map(({ results }) => results));
    }
}
